import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  Mesh,
  MeshStandardMaterial,
  Vector3,
} from 'three';
import { devBus } from '../debug/devBus';

// Low-resolution verlet cloth on the CPU.
//
//                        col 0   col 1   col 2  ...
//          row 0 (pin)   ●───────●───────●         ← pinned top row
//                        │  ╲ ╱  │  ╲ ╱  │             (gravity hangs the rest)
//                        ●───────●───────●         ← structural + shear constraints
//                        ...
//
// Algorithm: classic Jakobsen verlet — integrate, then iteratively project
// constraints. Three constraint flavors:
//   - structural: every particle to its 4-connected grid neighbors (the
//     load-bearing constraints; pulls cloth back to its rest shape)
//   - shear: every particle to its 4 diagonal neighbors (resists collapse)
//   - bend: every particle to its 2-away grid neighbors (resists folding —
//     keeps the cloth from looking like a paper airplane)
//
// Wind is a sum of sines that drifts in time, per-particle-modulated so
// different parts of the cloth wave at different phases. No noise lib
// dependency — three sines per axis is sufficient for "natural-looking
// turbulence" on a 12×8 grid.
//
// Grab: pin one particle to a target world position via grabAt(). Release
// with releaseGrab(). The constraint pass treats grabbed particles like
// pinned ones — neighbors drag along.

// ── Stability tunables (C2) ────────────────────────────────────────────────
// Named so retuning is an edit, not a hunt (C5 hygiene). These keep the verlet
// solver honest under abuse — fast drags, tab-away catch-up, era flips — so the
// worst case is a graceful settle, never an explosion or a rubber-band stretch.
//
// MAX_STRAIN: a post-solve clamp on structural edges — no load-bearing edge may
//   stretch past rest × this. Below the visible-rubber threshold; the shear/bend
//   edges are left free so the cloth still folds and drapes naturally.
const MAX_STRAIN = 1.12;
// MAX_STEP_DISP_FACTOR: per-substep displacement cap as a multiple of the
//   smaller structural rest length. A particle may not move more than this in
//   one substep — tames the energetic ripple on a hard release without
//   stiffening ordinary motion.
const MAX_STEP_DISP_FACTOR = 2.0;
// VELOCITY_SANITY_FACTOR: if any particle's per-substep speed exceeds this many
//   rest-lengths, the sim is diverging — trip the self-heal.
const VELOCITY_SANITY_FACTOR = 8.0;
// SETTLE_FRAMES: on a detected explosion (NaN/Inf or runaway velocity), ease the
//   whole cloth back toward its rest pose over this many frames with zero
//   velocity — self-healing beats a perfect-but-fragile solver.
const SETTLE_FRAMES = 12;
// SETTLE_LERP: per-frame blend toward rest while settling.
const SETTLE_LERP = 0.25;
// RESET_DIST_FACTOR: hard upper bound on how far a FREE particle may drift from
//   its rest position, as a multiple of the cloth's own diagonal. Past this it
//   is unconditionally snapped to rest (and the self-heal trips) — an absolute
//   bound so the cloth can never run away to infinity regardless of input. A
//   reachable grab (leashed in C4) stays well inside this; it only bites on
//   pathological input.
const RESET_DIST_FACTOR = 4.0;

// ── Static colliders (C3) ───────────────────────────────────────────────────
// Hand-authored static primitives the cloth cannot pass through (the only other
// collision is the floor-Y clamp). Resolved by analytic push-out after the
// constraint solve. Scope fence (per the workplan): static, hand-authored
// primitives only — no self-collision, mesh colliders, or moving colliders.
//
//   sphere   center + radius.
//   capsule  segment p0→p1 + radius (a swept sphere — good for table legs/arms).
//   box      axis-aligned min/max (the tabletop volume).
export type ClothCollider =
  | { type: 'sphere'; center: [number, number, number]; radius: number }
  | { type: 'capsule'; p0: [number, number, number]; p1: [number, number, number]; radius: number }
  | { type: 'box'; min: [number, number, number]; max: [number, number, number] };

// COLLIDER_THICKNESS: cloth rests this far OFF a collider surface (metres) — a
//   fabric-thickness offset that also avoids z-fighting the surface it drapes on.
const COLLIDER_THICKNESS = 0.012;
// COLLIDER_FRICTION: fraction of tangential velocity removed on contact so the
//   cloth rests against a surface instead of sliding off it. 0 = frictionless.
const COLLIDER_FRICTION = 0.5;

// ── Grab feel (C4) ──────────────────────────────────────────────────────────
// GRAB_RADIUS_FACTOR: a grab captures every particle within this many minimum
//   rest-lengths of the closest one, with preserved relative offsets — so it
//   reads as pinching a patch of fabric, not spiking one vertex into a tent.
const GRAB_RADIUS_FACTOR = 1.75;
// GRAB_MAX_PARTICLES: cap on how many particles one grab pins (keeps a fine
//   mesh from grabbing a huge sheet at once).
const GRAB_MAX_PARTICLES = 12;
// LEASH_FACTOR: the grabbed patch can be pulled at most this many cloth-
//   diagonals from where the grab began; past that the drag target is clamped
//   (resist), so dragging to the horizon can't stretch the cloth to spaghetti.
const LEASH_FACTOR = 1.5;

// ── Wind gust shape (C5 hygiene) ────────────────────────────────────────────
// The wind's strength + reach are the `windStrength` / `windAmplitude` options;
// these name the per-particle GUST envelope so retuning gustiness is an edit,
// not a hunt. mod = BASE + DEPTH·(spatial turbulence in −1..1): BASE is the
// always-on breeze floor, DEPTH the gust swing on top.
const WIND_GUST_BASE = 0.55;
const WIND_GUST_DEPTH = 0.45;

export interface ClothPinConfig {
  /** Indices to pin permanently. If omitted, the top row (row 0) is pinned. */
  indices?: number[];
}

export interface ClothPatchOptions {
  /** Short id for dev-bus events (`cloth:grab`, `cloth:release`). Optional —
   *  defaults to 'cloth'. Set a distinct name per instance (e.g.
   *  'tablecloth_front') so the event log can name what the user grabbed. */
  id?: string;
  /** Two world endpoints that define the cloth's TOP edge — pin row 0 runs
   *  evenly between them. Length = cloth width. */
  pinStart: Vector3;
  pinEnd: Vector3;
  /** How far the cloth hangs below the pin row, in metres. */
  height: number;
  /** Particles across width (cols ≥ 2). */
  cols?: number;
  /** Particles down height (rows ≥ 2). */
  rows?: number;
  /** Particles that should never move. Defaults to the top row. */
  pin?: ClothPinConfig;
  /** m/s² downward. ~9.8 is "real" but cloth feels heavy; ~3 reads as light
   *  fabric in still air. */
  gravity?: number;
  /** Velocity damping per tick. 0 = no friction; 0.01 = a wisp of air. */
  damping?: number;
  /** Multiplier on the wind acceleration. 0 = dead still. */
  windStrength?: number;
  /** Wind vector amplitude — clamps the per-axis peak forces. */
  windAmplitude?: Vector3;
  /** Constraint solver iterations per tick. 4–6 is the sweet spot; more
   *  iterations = stiffer cloth but more CPU. */
  iterations?: number;
  /** Material override. If omitted, a warm-cream cloth material is built. */
  material?: MeshStandardMaterial;
  /** Floor Y. Particles below this clamp on top each tick. */
  floorY?: number;
  /** Static colliders the cloth is pushed out of after each constraint pass
   *  (C3). Hand-authored primitives only. The floor plane (floorY) stays
   *  separate. */
  colliders?: ClothCollider[];
}

export class ClothPatch {
  readonly mesh: Mesh;

  private cols: number;
  private rows: number;
  private nParticles: number;

  // Position buffers — Float32Array because we hand the same buffer to
  // BufferAttribute and update in place each tick.
  private positions: Float32Array;
  private prev: Float32Array;
  private pinned: Uint8Array;
  // Pinned target — only used when pinned[i] === 1. Lets us move pins
  // around (e.g. for the grab interaction) without separate state.
  private pinPos: Float32Array;
  // Normals regenerated each tick for shading.
  private normals: Float32Array;

  // Constraint table — flat [a, b, restLen] triplets. Built once at construct,
  // burned through each iteration of the constraint pass.
  private constraints: Float32Array;
  private nConstraints: number;
  // Per-constraint kind, parallel to `constraints`: 0 = structural, 1 = shear,
  // 2 = bend. Only structural edges get the strain-limit clamp (C2).
  private constraintKind: Uint8Array;

  // Rest pose snapshot (the flat hang at construction) — the target the
  // self-heal eases toward when the sim diverges (C2).
  private restPositions: Float32Array;
  // Per-substep displacement cap in metres (derived from the rest lengths). 0
  // disables (set once in the constructor).
  private maxStepDisp = 0;
  private velSanitySq = Infinity;
  // Absolute drift bound (squared) — a free particle past this far from rest is
  // snapped back (C2 last-resort net).
  private resetDistSq = Infinity;
  // Frames remaining in a self-heal settle; >0 means "easing back to rest".
  private settleFrames = 0;

  /** Dev-bus event id. Public so consumers can rename after construction. */
  id: string;

  private gravity: number;
  private damping: number;
  private windStrength: number;
  private windAmp: Vector3;
  private iterations: number;
  private floorY: number;
  private colliders: ClothCollider[];
  private elapsed = 0;

  // Grab state (C4) — a grab pins a small PATCH of particles (the closest plus
  // its neighbors within a radius) and moves them as a rigid cluster with the
  // offsets they had at grab time, so it reads as pinching fabric. Uses the
  // same pinned[] machinery, toggling several indices in/out.
  //   grabIndex      the primary (closest) particle — drives the gizmo marker.
  //   grabParticles  every pinned-by-this-grab index.
  //   grabOffsets    flat [dx,dy,dz] per grabParticle: its position relative to
  //                  the grab origin at grab time (preserved as the cluster moves).
  //   grabWasPinnedArr  per grabParticle: was it already a permanent pin?
  //   grabOrigin     the world point the grab started at (leash anchor).
  private grabIndex = -1;
  private grabParticles: number[] = [];
  private grabOffsets: number[] = [];
  private grabWasPinnedArr: boolean[] = [];
  private grabOrigin = new Vector3();
  // Derived grab tunables (set in the constructor from the grid scale).
  private grabRadius = 0;
  private leashDist = 0;

  constructor(opts: ClothPatchOptions) {
    this.id = opts.id ?? 'cloth';
    this.cols = Math.max(2, opts.cols ?? 14);
    this.rows = Math.max(2, opts.rows ?? 10);
    this.nParticles = this.cols * this.rows;
    this.gravity = opts.gravity ?? 3.2;
    this.damping = opts.damping ?? 0.018;
    this.windStrength = opts.windStrength ?? 1.0;
    this.windAmp = opts.windAmplitude?.clone() ?? new Vector3(0.55, 0.1, 0.4);
    this.iterations = opts.iterations ?? 5;
    this.floorY = opts.floorY ?? -Infinity;
    this.colliders = opts.colliders ?? [];

    // ── particle init ───────────────────────────────────────────────────
    this.positions = new Float32Array(this.nParticles * 3);
    this.prev = new Float32Array(this.nParticles * 3);
    this.pinned = new Uint8Array(this.nParticles);
    this.pinPos = new Float32Array(this.nParticles * 3);
    this.normals = new Float32Array(this.nParticles * 3);

    // Place particles in a flat grid hanging straight down from the pin
    // edge. The first tick of constraints will settle the natural slack —
    // structural rest length is HEIGHT/(rows-1), so the initial straight
    // hang is exactly at rest. Wind/gravity ripple it from there.
    const pinDir = new Vector3().subVectors(opts.pinEnd, opts.pinStart);
    const width = pinDir.length();
    pinDir.normalize();
    const downDir = new Vector3(0, -1, 0);

    const colSpan = width;
    const rowSpan = opts.height;
    for (let row = 0; row < this.rows; row++) {
      const v = row / (this.rows - 1);
      for (let col = 0; col < this.cols; col++) {
        const u = col / (this.cols - 1);
        const x = opts.pinStart.x + pinDir.x * (colSpan * u) + downDir.x * (rowSpan * v);
        const y = opts.pinStart.y + pinDir.y * (colSpan * u) + downDir.y * (rowSpan * v);
        const z = opts.pinStart.z + pinDir.z * (colSpan * u) + downDir.z * (rowSpan * v);
        const i = (row * this.cols + col) * 3;
        this.positions[i + 0] = x;
        this.positions[i + 1] = y;
        this.positions[i + 2] = z;
        this.prev[i + 0] = x;
        this.prev[i + 1] = y;
        this.prev[i + 2] = z;
        this.pinPos[i + 0] = x;
        this.pinPos[i + 1] = y;
        this.pinPos[i + 2] = z;
      }
    }

    // Default pin: every particle in row 0.
    if (opts.pin?.indices) {
      for (const idx of opts.pin.indices) this.pinned[idx] = 1;
    } else {
      for (let col = 0; col < this.cols; col++) {
        this.pinned[col] = 1; // row 0 indices are 0..cols-1
      }
    }

    // ── constraints ─────────────────────────────────────────────────────
    // Estimate the maximum constraint count for the typed array. Worst case:
    //   structural = ~2*N (each particle has up to 2 unique edges: right + down)
    //   shear      = ~2*N (two unique diagonals per cell)
    //   bend       = ~2*N (two-away neighbors right/down)
    // 6*N is comfortably above; we trim at the end.
    const maxC = this.nParticles * 6 + 32;
    this.constraints = new Float32Array(maxC * 3);
    this.constraintKind = new Uint8Array(maxC);
    let n = 0;
    const restCol = colSpan / (this.cols - 1);
    const restRow = rowSpan / (this.rows - 1);
    const restDiag = Math.sqrt(restCol * restCol + restRow * restRow);
    const addC = (a: number, b: number, rest: number, kind: number): void => {
      this.constraints[n * 3 + 0] = a;
      this.constraints[n * 3 + 1] = b;
      this.constraints[n * 3 + 2] = rest;
      this.constraintKind[n] = kind;
      n++;
    };
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const i = row * this.cols + col;
        // Structural: right, down (kind 0 — load-bearing, strain-limited)
        if (col + 1 < this.cols) addC(i, row * this.cols + (col + 1), restCol, 0);
        if (row + 1 < this.rows) addC(i, (row + 1) * this.cols + col, restRow, 0);
        // Shear: diagonal-right-down, diagonal-left-down (kind 1)
        if (col + 1 < this.cols && row + 1 < this.rows) {
          addC(i, (row + 1) * this.cols + (col + 1), restDiag, 1);
        }
        if (col - 1 >= 0 && row + 1 < this.rows) {
          addC(i, (row + 1) * this.cols + (col - 1), restDiag, 1);
        }
        // Bend: 2-away right, 2-away down (kind 2)
        if (col + 2 < this.cols) addC(i, row * this.cols + (col + 2), restCol * 2, 2);
        if (row + 2 < this.rows) addC(i, (row + 2) * this.cols + col, restRow * 2, 2);
      }
    }
    this.nConstraints = n;

    // Stability caps derived from the grid scale (C2). The displacement cap is a
    // multiple of the smaller structural rest length; the velocity-sanity bound
    // (compared squared, against per-substep displacement) trips the self-heal.
    const minRest = Math.min(restCol, restRow);
    this.maxStepDisp = minRest * MAX_STEP_DISP_FACTOR;
    const velSanity = minRest * VELOCITY_SANITY_FACTOR;
    this.velSanitySq = velSanity * velSanity;
    const clothDiag = Math.sqrt(colSpan * colSpan + rowSpan * rowSpan);
    const resetDist = clothDiag * RESET_DIST_FACTOR;
    this.resetDistSq = resetDist * resetDist;
    this.grabRadius = minRest * GRAB_RADIUS_FACTOR;
    this.leashDist = clothDiag * LEASH_FACTOR;
    // Rest-pose snapshot for the self-heal target.
    this.restPositions = this.positions.slice();

    // ── geometry / mesh ─────────────────────────────────────────────────
    const geom = new BufferGeometry();
    geom.setAttribute('position', new BufferAttribute(this.positions, 3));
    geom.setAttribute('normal', new BufferAttribute(this.normals, 3));

    const uvs = new Float32Array(this.nParticles * 2);
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const i = row * this.cols + col;
        uvs[i * 2 + 0] = col / (this.cols - 1);
        uvs[i * 2 + 1] = 1 - row / (this.rows - 1);
      }
    }
    geom.setAttribute('uv', new BufferAttribute(uvs, 2));

    // Index buffer — two tris per cell. Uint16 is fine for up to 65k verts;
    // a 12×8 cloth has 96.
    const cells = (this.cols - 1) * (this.rows - 1);
    const indices = new Uint16Array(cells * 6);
    let idx = 0;
    for (let row = 0; row < this.rows - 1; row++) {
      for (let col = 0; col < this.cols - 1; col++) {
        const tl = row * this.cols + col;
        const tr = tl + 1;
        const bl = tl + this.cols;
        const br = bl + 1;
        indices[idx++] = tl; indices[idx++] = bl; indices[idx++] = tr;
        indices[idx++] = tr; indices[idx++] = bl; indices[idx++] = br;
      }
    }
    geom.setIndex(new BufferAttribute(indices, 1));
    geom.computeBoundingSphere();

    const material =
      opts.material ??
      new MeshStandardMaterial({
        color: new Color(0xb88a5e),
        roughness: 0.95,
        metalness: 0,
        side: DoubleSide,
      });

    this.mesh = new Mesh(geom, material);
    this.mesh.frustumCulled = false; // bounds change every tick; saves a recompute
    this.mesh.renderOrder = 1;
    // Cloth is intentionally raycastable (so the grab can hit it) but
    // skips the inverted-hull outline pass (it's an open surface; the hull
    // would render as a fat halo behind every triangle).
    this.mesh.userData.no_outline = true;
    this.mesh.userData.no_highlight = true; // no hover/select emissive on cloth (John) — the grab cursor still works
    this.mesh.userData.is_cloth = true;
    // Recompute normals once so the first frame isn't flat-shaded blank.
    this.computeNormals();
  }

  // Public API ──────────────────────────────────────────────────────────

  /** Drive the sim forward by `dt` seconds. Sub-steps if `dt` is large so a
   *  tabbed-away pause doesn't blow the cloth across the world on resume. */
  tick(dt: number): void {
    // Cap dt to keep things stable — verlet has no inherent step size limit
    // but the constraint solver explodes if particles teleport more than a
    // constraint length per step. 1/30 ≈ 33ms is safe for our geometry.
    const maxStep = 1 / 30;
    let remaining = Math.min(dt, 0.1); // cap at 100ms — beyond that, just drop frames
    while (remaining > 0) {
      const step = Math.min(remaining, maxStep);
      this.simStep(step);
      remaining -= step;
    }
    this.computeNormals();
    (this.mesh.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;
    (this.mesh.geometry.getAttribute('normal') as BufferAttribute).needsUpdate = true;
    // Refit the bounding sphere so raycasting tracks the deformed cloth. It is
    // computed once at construction and Mesh.raycast() rejects the ray against
    // that stale sphere first — so a cloth that hangs/blows outside its rest
    // sphere becomes unhittable exactly where it visibly is (the "can't grab
    // it" bug). At N≈100–240 verts a per-tick refit is micro-cost.
    this.mesh.geometry.computeBoundingSphere();
  }

  /** Rigidly shift the ENTIRE cloth — every particle, its pins, its rest pose,
   *  its floor, and its colliders — by a world-space delta. Carries the cloth
   *  along when the hero it drapes is repositioned (item 7): the drape and the
   *  table collider stay aligned, no re-pin or rebuild, the sim continues from
   *  the shifted state. No-op for a zero delta. */
  translate(dx: number, dy: number, dz: number): void {
    if (dx === 0 && dy === 0 && dz === 0) return;
    for (let i = 0; i < this.nParticles; i++) {
      const ix = i * 3;
      this.positions[ix] += dx; this.positions[ix + 1] += dy; this.positions[ix + 2] += dz;
      this.prev[ix] += dx; this.prev[ix + 1] += dy; this.prev[ix + 2] += dz;
      this.pinPos[ix] += dx; this.pinPos[ix + 1] += dy; this.pinPos[ix + 2] += dz;
      this.restPositions[ix] += dx; this.restPositions[ix + 1] += dy; this.restPositions[ix + 2] += dz;
    }
    for (const c of this.colliders) {
      if (c.type === 'box') { c.min[0] += dx; c.min[1] += dy; c.min[2] += dz; c.max[0] += dx; c.max[1] += dy; c.max[2] += dz; }
      else if (c.type === 'sphere') { c.center[0] += dx; c.center[1] += dy; c.center[2] += dz; }
      else { c.p0[0] += dx; c.p0[1] += dy; c.p0[2] += dz; c.p1[0] += dx; c.p1[1] += dy; c.p1[2] += dz; }
    }
    if (this.floorY > -Infinity) this.floorY += dy; // the rest level rides vertical moves with the table
    this.grabOrigin.set(this.grabOrigin.x + dx, this.grabOrigin.y + dy, this.grabOrigin.z + dz);
    (this.mesh.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;
    this.mesh.geometry.computeBoundingSphere();
  }

  /** Grab the patch of fabric nearest `worldPos` (C4): pin the closest particle
   *  plus every neighbor within the grab radius, preserving their relative
   *  offsets so the cluster moves as a pinched patch rather than one spike.
   *  Releases any previous grab. Returns the primary particle index, or -1.
   *
   *  B4: already-pinned particles (the anchor seam — the top/pinned row) are
   *  excluded from BOTH the primary nearest search and the neighbor patch. The
   *  grabbable set is the free cloth, not the seam, so a grab can never relocate
   *  a seam particle's pinPos and tear the cloth off its anchor. */
  grabAt(worldPos: Vector3): number {
    this.releaseGrab();
    let best = -1;
    let bestDist = Infinity;
    for (let i = 0; i < this.nParticles; i++) {
      if (this.pinned[i] === 1) continue; // B4: never start a grab on the pinned seam
      const dx = this.positions[i * 3 + 0] - worldPos.x;
      const dy = this.positions[i * 3 + 1] - worldPos.y;
      const dz = this.positions[i * 3 + 2] - worldPos.z;
      const d = dx * dx + dy * dy + dz * dz;
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    if (best < 0) return -1;

    this.grabIndex = best;
    this.grabOrigin.copy(worldPos);

    // Pin the primary plus neighbors within grabRadius of the PRIMARY particle,
    // nearest-first up to the cap. Each records its offset from the grab origin
    // so the cluster translates rigidly under moveGrab.
    const bx = this.positions[best * 3 + 0];
    const by = this.positions[best * 3 + 1];
    const bz = this.positions[best * 3 + 2];
    const radiusSq = this.grabRadius * this.grabRadius;
    const candidates: Array<{ i: number; d: number }> = [];
    for (let i = 0; i < this.nParticles; i++) {
      if (this.pinned[i] === 1) continue; // B4: keep pinned seam particles out of the grabbed patch
      const dx = this.positions[i * 3 + 0] - bx;
      const dy = this.positions[i * 3 + 1] - by;
      const dz = this.positions[i * 3 + 2] - bz;
      const d = dx * dx + dy * dy + dz * dz;
      if (d <= radiusSq) candidates.push({ i, d });
    }
    candidates.sort((a, b) => a.d - b.d);
    const take = Math.min(candidates.length, GRAB_MAX_PARTICLES);
    for (let c = 0; c < take; c++) {
      const i = candidates[c].i;
      this.grabParticles.push(i);
      this.grabWasPinnedArr.push(this.pinned[i] === 1);
      this.grabOffsets.push(
        this.positions[i * 3 + 0] - worldPos.x,
        this.positions[i * 3 + 1] - worldPos.y,
        this.positions[i * 3 + 2] - worldPos.z,
      );
      this.pinned[i] = 1;
      this.pinPos[i * 3 + 0] = this.positions[i * 3 + 0];
      this.pinPos[i * 3 + 1] = this.positions[i * 3 + 1];
      this.pinPos[i * 3 + 2] = this.positions[i * 3 + 2];
    }
    devBus.emit('cloth:grab', { cloth: this.id, particle: best });
    return best;
  }

  /** Move the grabbed patch. The target is leashed to within LEASH_FACTOR cloth
   *  diagonals of the grab origin (resist) so a drag to the horizon can't
   *  stretch the cloth to spaghetti; each grabbed particle tracks the leashed
   *  target plus its captured offset. No-op if nothing's grabbed. */
  moveGrab(worldPos: Vector3): void {
    if (this.grabIndex < 0) return;
    // Leash: clamp the target into a sphere of radius leashDist about the origin.
    let tx = worldPos.x, ty = worldPos.y, tz = worldPos.z;
    const dx = tx - this.grabOrigin.x, dy = ty - this.grabOrigin.y, dz = tz - this.grabOrigin.z;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq > this.leashDist * this.leashDist) {
      const s = this.leashDist / Math.sqrt(distSq);
      tx = this.grabOrigin.x + dx * s;
      ty = this.grabOrigin.y + dy * s;
      tz = this.grabOrigin.z + dz * s;
    }
    for (let c = 0; c < this.grabParticles.length; c++) {
      const i = this.grabParticles[c];
      this.pinPos[i * 3 + 0] = tx + this.grabOffsets[c * 3 + 0];
      this.pinPos[i * 3 + 1] = ty + this.grabOffsets[c * 3 + 1];
      this.pinPos[i * 3 + 2] = tz + this.grabOffsets[c * 3 + 2];
    }
  }

  /** Release the current grab. Restores each grabbed particle's prior pinned
   *  state (so grabbing near a permanently-pinned corner doesn't unpin it). */
  releaseGrab(): void {
    if (this.grabIndex < 0) return;
    for (let c = 0; c < this.grabParticles.length; c++) {
      if (!this.grabWasPinnedArr[c]) this.pinned[this.grabParticles[c]] = 0;
    }
    this.grabParticles.length = 0;
    this.grabOffsets.length = 0;
    this.grabWasPinnedArr.length = 0;
    this.grabIndex = -1;
    devBus.emit('cloth:release', { cloth: this.id });
  }

  isGrabbing(): boolean {
    return this.grabIndex >= 0;
  }

  /** How many particles the current grab pins (the pinched patch size, C4). */
  get grabbedCount(): number {
    return this.grabParticles.length;
  }

  /** True while the cloth is self-healing back to rest after a detected
   *  divergence (C2). Exposed for gizmos/diagnostics. */
  get isSettling(): boolean {
    return this.settleFrames > 0;
  }

  /** Position by particle index — used by the grab controller to compute the
   *  initial grab plane. */
  getParticlePosition(i: number, out: Vector3): Vector3 {
    return out.set(
      this.positions[i * 3 + 0],
      this.positions[i * 3 + 1],
      this.positions[i * 3 + 2],
    );
  }

  // ── debug accessors (Phase 2 gizmo wireframe) ────────────────────────
  //
  // The cloth's internal Float32Arrays are reused as BufferAttribute backing
  // for the wireframe gizmo — same memory, marked dirty each tick by the
  // gizmo so it follows the sim with zero copy. These accessors expose just
  // enough to build that wireframe without leaking write access.

  /** Particle positions, three floats per particle (x,y,z). Shared by ref. */
  get positionsBuffer(): Float32Array { return this.positions; }
  /** 1 = pinned, 0 = free. Used to colour pin markers. */
  get pinnedFlags(): Uint8Array { return this.pinned; }
  /** Total particle count (cols × rows). */
  get particleCount(): number { return this.nParticles; }
  /** Currently grabbed particle index, or -1 if not grabbed. */
  get currentGrabIndex(): number { return this.grabIndex; }

  /** Build a flat Uint16 index array suitable for LineSegments — pairs of
   *  particle indices, one pair per constraint. Allocated once per call;
   *  consumers should cache. */
  buildConstraintLineIndices(): Uint16Array {
    const out = new Uint16Array(this.nConstraints * 2);
    for (let c = 0; c < this.nConstraints; c++) {
      out[c * 2 + 0] = this.constraints[c * 3 + 0];
      out[c * 2 + 1] = this.constraints[c * 3 + 1];
    }
    return out;
  }

  // Private ─────────────────────────────────────────────────────────────

  private simStep(dt: number): void {
    this.elapsed += dt;

    // Self-heal (C2): while settling, skip physics and ease every free particle
    // back toward its rest pose with zero velocity. Pinned particles (including
    // an active grab) hold their target so a held cloth recovers around the
    // hand. A graceful settle beats a perfect-but-fragile solver.
    if (this.settleFrames > 0) {
      for (let i = 0; i < this.nParticles; i++) {
        const ix = i * 3;
        if (this.pinned[i]) {
          this.positions[ix + 0] = this.pinPos[ix + 0];
          this.positions[ix + 1] = this.pinPos[ix + 1];
          this.positions[ix + 2] = this.pinPos[ix + 2];
        } else {
          this.positions[ix + 0] += (this.restPositions[ix + 0] - this.positions[ix + 0]) * SETTLE_LERP;
          this.positions[ix + 1] += (this.restPositions[ix + 1] - this.positions[ix + 1]) * SETTLE_LERP;
          this.positions[ix + 2] += (this.restPositions[ix + 2] - this.positions[ix + 2]) * SETTLE_LERP;
        }
        // Zero velocity (verlet velocity = pos − prev).
        this.prev[ix + 0] = this.positions[ix + 0];
        this.prev[ix + 1] = this.positions[ix + 1];
        this.prev[ix + 2] = this.positions[ix + 2];
      }
      this.settleFrames--;
      return;
    }

    // Wind: three sines per axis, time-evolving direction. Per-particle
    // spatial modulation gives different parts of the cloth different
    // phases without needing a noise function.
    const t = this.elapsed;
    const windDirX = Math.sin(t * 0.6) * 0.7 + Math.sin(t * 0.13) * 0.3;
    const windDirY = Math.sin(t * 1.1) * 0.4 + Math.cos(t * 0.27) * 0.6;
    const windDirZ = Math.cos(t * 0.5) * 0.7 + Math.sin(t * 0.21) * 0.3;
    const wx = this.windAmp.x * this.windStrength * windDirX;
    const wy = this.windAmp.y * this.windStrength * windDirY;
    const wz = this.windAmp.z * this.windStrength * windDirZ;

    const damping = 1 - this.damping;
    const dt2 = dt * dt;
    const gAcc = -this.gravity;

    // Integrate (verlet).
    for (let i = 0; i < this.nParticles; i++) {
      if (this.pinned[i]) {
        this.positions[i * 3 + 0] = this.pinPos[i * 3 + 0];
        this.positions[i * 3 + 1] = this.pinPos[i * 3 + 1];
        this.positions[i * 3 + 2] = this.pinPos[i * 3 + 2];
        this.prev[i * 3 + 0] = this.pinPos[i * 3 + 0];
        this.prev[i * 3 + 1] = this.pinPos[i * 3 + 1];
        this.prev[i * 3 + 2] = this.pinPos[i * 3 + 2];
        continue;
      }
      const px = this.positions[i * 3 + 0];
      const py = this.positions[i * 3 + 1];
      const pz = this.positions[i * 3 + 2];

      // Per-particle wind modulation: 0..1 phase tied to particle's own
      // world position. Two sines combined so different rows + cols pick
      // up the wind at different moments.
      const modX = Math.sin(px * 1.4 + t * 1.7);
      const modZ = Math.cos(pz * 1.2 + t * 1.1);
      const mod = WIND_GUST_BASE + WIND_GUST_DEPTH * modX * modZ;

      const vx = (px - this.prev[i * 3 + 0]) * damping;
      const vy = (py - this.prev[i * 3 + 1]) * damping;
      const vz = (pz - this.prev[i * 3 + 2]) * damping;
      this.prev[i * 3 + 0] = px;
      this.prev[i * 3 + 1] = py;
      this.prev[i * 3 + 2] = pz;
      this.positions[i * 3 + 0] = px + vx + wx * mod * dt2;
      this.positions[i * 3 + 1] = py + vy + (gAcc + wy * mod) * dt2;
      this.positions[i * 3 + 2] = pz + vz + wz * mod * dt2;
    }

    // Solve constraints.
    for (let iter = 0; iter < this.iterations; iter++) {
      for (let c = 0; c < this.nConstraints; c++) {
        const a = this.constraints[c * 3 + 0];
        const b = this.constraints[c * 3 + 1];
        const rest = this.constraints[c * 3 + 2];
        const ai = a * 3;
        const bi = b * 3;
        const dx = this.positions[bi + 0] - this.positions[ai + 0];
        const dy = this.positions[bi + 1] - this.positions[ai + 1];
        const dz = this.positions[bi + 2] - this.positions[ai + 2];
        const distSq = dx * dx + dy * dy + dz * dz;
        if (distSq === 0) continue;
        const dist = Math.sqrt(distSq);
        const diff = ((dist - rest) / dist) * 0.5;
        const ox = dx * diff;
        const oy = dy * diff;
        const oz = dz * diff;
        const aPinned = this.pinned[a];
        const bPinned = this.pinned[b];
        if (aPinned && bPinned) continue;
        if (aPinned) {
          // All correction goes to b.
          this.positions[bi + 0] -= ox * 2;
          this.positions[bi + 1] -= oy * 2;
          this.positions[bi + 2] -= oz * 2;
        } else if (bPinned) {
          this.positions[ai + 0] += ox * 2;
          this.positions[ai + 1] += oy * 2;
          this.positions[ai + 2] += oz * 2;
        } else {
          this.positions[ai + 0] += ox;
          this.positions[ai + 1] += oy;
          this.positions[ai + 2] += oz;
          this.positions[bi + 0] -= ox;
          this.positions[bi + 1] -= oy;
          this.positions[bi + 2] -= oz;
        }
      }

      // Floor: clamp after constraints — solving constraints first respects
      // the cloth's natural shape, then a single clamp pass keeps it above
      // the floor.
      if (this.floorY > -Infinity) {
        for (let i = 0; i < this.nParticles; i++) {
          if (this.positions[i * 3 + 1] < this.floorY) {
            this.positions[i * 3 + 1] = this.floorY;
          }
        }
      }

      // Static colliders: push particles out of authored primitives. Friction
      // is applied only on the final iteration (decaying tangential velocity
      // once per substep, not five times) so the cloth rests instead of slides.
      if (this.colliders.length > 0) {
        this.projectColliders(iter === this.iterations - 1);
      }
    }

    // ── post-solve stability passes (C2) ───────────────────────────────────
    // Strain limit: one pass over structural (load-bearing) edges only. Any
    // edge stretched past rest × MAX_STRAIN is pulled back to that ceiling, so
    // a fast drag reads as taut fabric instead of rubber. Shear/bend edges are
    // left free to keep folds and drape natural.
    const p = this.positions;
    for (let c = 0; c < this.nConstraints; c++) {
      if (this.constraintKind[c] !== 0) continue;
      const a = this.constraints[c * 3 + 0];
      const b = this.constraints[c * 3 + 1];
      const maxLen = this.constraints[c * 3 + 2] * MAX_STRAIN;
      const ai = a * 3;
      const bi = b * 3;
      const dx = p[bi + 0] - p[ai + 0];
      const dy = p[bi + 1] - p[ai + 1];
      const dz = p[bi + 2] - p[ai + 2];
      const distSq = dx * dx + dy * dy + dz * dz;
      if (distSq <= maxLen * maxLen || distSq === 0) continue;
      const dist = Math.sqrt(distSq);
      // Move endpoints inward so the edge is exactly maxLen, honoring pins.
      const excess = (dist - maxLen) / dist;
      const aPinned = this.pinned[a];
      const bPinned = this.pinned[b];
      if (aPinned && bPinned) continue;
      if (aPinned) {
        p[bi + 0] -= dx * excess; p[bi + 1] -= dy * excess; p[bi + 2] -= dz * excess;
      } else if (bPinned) {
        p[ai + 0] += dx * excess; p[ai + 1] += dy * excess; p[ai + 2] += dz * excess;
      } else {
        const h = excess * 0.5;
        p[ai + 0] += dx * h; p[ai + 1] += dy * h; p[ai + 2] += dz * h;
        p[bi + 0] -= dx * h; p[bi + 1] -= dy * h; p[bi + 2] -= dz * h;
      }
    }

    // Per-substep displacement cap + divergence guard. A free particle may not
    // move more than maxStepDisp from where it began the substep (prev); past
    // that the move is scaled back, which also caps the implied velocity. If
    // any particle is non-finite or moved past the sanity bound, the solver is
    // diverging — trip the self-heal.
    const capSq = this.maxStepDisp * this.maxStepDisp;
    let diverged = false;
    for (let i = 0; i < this.nParticles; i++) {
      if (this.pinned[i]) continue;
      const ix = i * 3;
      const x = p[ix + 0], y = p[ix + 1], z = p[ix + 2];
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) { diverged = true; continue; }
      // Absolute drift bound: a free particle that has wandered past
      // resetDist from its rest is snapped home (and trips the self-heal),
      // so nothing can run away to infinity even under pathological input.
      const rdx = x - this.restPositions[ix + 0];
      const rdy = y - this.restPositions[ix + 1];
      const rdz = z - this.restPositions[ix + 2];
      if (rdx * rdx + rdy * rdy + rdz * rdz > this.resetDistSq) {
        p[ix + 0] = this.restPositions[ix + 0];
        p[ix + 1] = this.restPositions[ix + 1];
        p[ix + 2] = this.restPositions[ix + 2];
        this.prev[ix + 0] = p[ix + 0];
        this.prev[ix + 1] = p[ix + 1];
        this.prev[ix + 2] = p[ix + 2];
        diverged = true;
        continue;
      }
      const dx = x - this.prev[ix + 0];
      const dy = y - this.prev[ix + 1];
      const dz = z - this.prev[ix + 2];
      const moveSq = dx * dx + dy * dy + dz * dz;
      if (moveSq > this.velSanitySq) { diverged = true; }
      if (this.maxStepDisp > 0 && moveSq > capSq) {
        const s = this.maxStepDisp / Math.sqrt(moveSq);
        p[ix + 0] = this.prev[ix + 0] + dx * s;
        p[ix + 1] = this.prev[ix + 1] + dy * s;
        p[ix + 2] = this.prev[ix + 2] + dz * s;
      }
    }

    if (diverged) {
      // Hard-reset any non-finite particle to rest immediately (NaN would
      // otherwise poison the settle lerp), then ease the rest back over a few
      // frames.
      for (let i = 0; i < this.nParticles; i++) {
        const ix = i * 3;
        if (!Number.isFinite(p[ix]) || !Number.isFinite(p[ix + 1]) || !Number.isFinite(p[ix + 2])) {
          p[ix + 0] = this.restPositions[ix + 0];
          p[ix + 1] = this.restPositions[ix + 1];
          p[ix + 2] = this.restPositions[ix + 2];
        }
        this.prev[ix + 0] = p[ix + 0];
        this.prev[ix + 1] = p[ix + 1];
        this.prev[ix + 2] = p[ix + 2];
      }
      this.settleFrames = SETTLE_FRAMES;
    }
  }

  /** Push free particles out of the static colliders (C3). Analytic per
   *  primitive; with `applyFriction`, damp the tangential velocity on contact
   *  so the cloth rests instead of sliding. Pinned particles are skipped. */
  private projectColliders(applyFriction: boolean): void {
    const p = this.positions;
    const prev = this.prev;
    const T = COLLIDER_THICKNESS;
    for (let i = 0; i < this.nParticles; i++) {
      if (this.pinned[i]) continue;
      const ix = i * 3;
      let px = p[ix + 0], py = p[ix + 1], pz = p[ix + 2];
      for (const col of this.colliders) {
        let nx = 0, ny = 0, nz = 0, pen = false;
        if (col.type === 'sphere') {
          const dx = px - col.center[0], dy = py - col.center[1], dz = pz - col.center[2];
          const distSq = dx * dx + dy * dy + dz * dz;
          const r = col.radius + T;
          if (distSq < r * r) {
            const dist = Math.sqrt(distSq) || 1e-6;
            nx = dx / dist; ny = dy / dist; nz = dz / dist;
            px = col.center[0] + nx * r; py = col.center[1] + ny * r; pz = col.center[2] + nz * r;
            pen = true;
          }
        } else if (col.type === 'capsule') {
          // Closest point on the segment p0→p1, then resolve like a sphere there.
          const ax = col.p0[0], ay = col.p0[1], az = col.p0[2];
          const abx = col.p1[0] - ax, aby = col.p1[1] - ay, abz = col.p1[2] - az;
          const abLenSq = abx * abx + aby * aby + abz * abz || 1e-9;
          let s = ((px - ax) * abx + (py - ay) * aby + (pz - az) * abz) / abLenSq;
          s = s < 0 ? 0 : s > 1 ? 1 : s;
          const cx = ax + abx * s, cy = ay + aby * s, cz = az + abz * s;
          const dx = px - cx, dy = py - cy, dz = pz - cz;
          const distSq = dx * dx + dy * dy + dz * dz;
          const r = col.radius + T;
          if (distSq < r * r) {
            const dist = Math.sqrt(distSq) || 1e-6;
            nx = dx / dist; ny = dy / dist; nz = dz / dist;
            px = cx + nx * r; py = cy + ny * r; pz = cz + nz * r;
            pen = true;
          }
        } else {
          // Axis-aligned box, expanded by thickness. If inside, push out along
          // the axis of least penetration (the nearest face).
          const minx = col.min[0] - T, miny = col.min[1] - T, minz = col.min[2] - T;
          const maxx = col.max[0] + T, maxy = col.max[1] + T, maxz = col.max[2] + T;
          if (px > minx && px < maxx && py > miny && py < maxy && pz > minz && pz < maxz) {
            let m = px - minx, axis = 0, sign = -1;
            if (maxx - px < m) { m = maxx - px; axis = 0; sign = 1; }
            if (py - miny < m) { m = py - miny; axis = 1; sign = -1; }
            if (maxy - py < m) { m = maxy - py; axis = 1; sign = 1; }
            if (pz - minz < m) { m = pz - minz; axis = 2; sign = -1; }
            if (maxz - pz < m) { m = maxz - pz; axis = 2; sign = 1; }
            if (axis === 0) { px = sign < 0 ? minx : maxx; nx = sign; }
            else if (axis === 1) { py = sign < 0 ? miny : maxy; ny = sign; }
            else { pz = sign < 0 ? minz : maxz; nz = sign; }
            pen = true;
          }
        }
        if (pen && applyFriction) {
          // Decompose velocity (pos − prev) into normal + tangential; keep the
          // normal part, scale the tangential down by COLLIDER_FRICTION.
          const vx = px - prev[ix + 0], vy = py - prev[ix + 1], vz = pz - prev[ix + 2];
          const vn = vx * nx + vy * ny + vz * nz;
          const k = 1 - COLLIDER_FRICTION;
          const nvx = vn * nx + (vx - vn * nx) * k;
          const nvy = vn * ny + (vy - vn * ny) * k;
          const nvz = vn * nz + (vz - vn * nz) * k;
          prev[ix + 0] = px - nvx; prev[ix + 1] = py - nvy; prev[ix + 2] = pz - nvz;
        }
      }
      p[ix + 0] = px; p[ix + 1] = py; p[ix + 2] = pz;
    }
  }

  /** The static colliders, for the gizmo layer to draw (C5). Read-only. */
  get colliderList(): readonly ClothCollider[] {
    return this.colliders;
  }

  private computeNormals(): void {
    // Build per-vertex normal by averaging adjacent triangle normals.
    // Cheap at N=96. Iterate cells, accumulate face normals onto each
    // of the 3 (or 4) verts they touch.
    this.normals.fill(0);
    const p = this.positions;
    const accN = (i: number, nx: number, ny: number, nz: number): void => {
      this.normals[i + 0] += nx;
      this.normals[i + 1] += ny;
      this.normals[i + 2] += nz;
    };
    const cross = (ax: number, ay: number, az: number, bx: number, by: number, bz: number): [number, number, number] => {
      return [ay * bz - az * by, az * bx - ax * bz, ax * by - ay * bx];
    };
    for (let row = 0; row < this.rows - 1; row++) {
      for (let col = 0; col < this.cols - 1; col++) {
        const tl = (row * this.cols + col) * 3;
        const tr = tl + 3;
        const bl = ((row + 1) * this.cols + col) * 3;
        const br = bl + 3;
        // Triangle 1: tl, bl, tr
        let ex = p[bl + 0] - p[tl + 0];
        let ey = p[bl + 1] - p[tl + 1];
        let ez = p[bl + 2] - p[tl + 2];
        let fx = p[tr + 0] - p[tl + 0];
        let fy = p[tr + 1] - p[tl + 1];
        let fz = p[tr + 2] - p[tl + 2];
        let [nx, ny, nz] = cross(ex, ey, ez, fx, fy, fz);
        accN(tl, nx, ny, nz);
        accN(bl, nx, ny, nz);
        accN(tr, nx, ny, nz);
        // Triangle 2: tr, bl, br
        ex = p[bl + 0] - p[tr + 0];
        ey = p[bl + 1] - p[tr + 1];
        ez = p[bl + 2] - p[tr + 2];
        fx = p[br + 0] - p[tr + 0];
        fy = p[br + 1] - p[tr + 1];
        fz = p[br + 2] - p[tr + 2];
        [nx, ny, nz] = cross(ex, ey, ez, fx, fy, fz);
        accN(tr, nx, ny, nz);
        accN(bl, nx, ny, nz);
        accN(br, nx, ny, nz);
      }
    }
    // Normalize.
    for (let i = 0; i < this.nParticles; i++) {
      const ni = i * 3;
      const nx = this.normals[ni + 0];
      const ny = this.normals[ni + 1];
      const nz = this.normals[ni + 2];
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      if (len > 0) {
        const inv = 1 / len;
        this.normals[ni + 0] = nx * inv;
        this.normals[ni + 1] = ny * inv;
        this.normals[ni + 2] = nz * inv;
      } else {
        this.normals[ni + 1] = 1;
      }
    }
  }
}
